import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { ArrowRight, Sparkles, Leaf, Lightbulb, Heart, Users, Cog } from "lucide-react";
import { EASE, scrollToId } from "./shared";
import { useLang } from "./i18n";

const MaskLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.15, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const ORBITS = [
  { Icon: Lightbulb, color: "#F5A524", pos: "-top-5 -left-6", dur: 4.2 },
  { Icon: Heart, color: "#E84393", pos: "-top-7 right-10", dur: 5 },
  { Icon: Leaf, color: "#3AA64B", pos: "top-1/3 -right-8", dur: 4.6 },
  { Icon: Cog, color: "#7C3AED", pos: "bottom-16 -left-9", dur: 5.4 },
  { Icon: Users, color: "#17B6E0", pos: "-bottom-6 right-1/4", dur: 4.9 },
];

export default function Hero({ onStart, copy }) {
  const { lang, t } = useLang();
  const cms = lang === "en" && copy ? copy : null;
  const h1a = cms?.h1a || t.h1a;
  const h1b = cms?.h1b || t.h1b;
  const h1c = cms?.h1c || t.h1c;
  const heroSub = cms?.sub || t.sub;
  const ref = useRef(null);
  const rx = useSpring(0, { stiffness: 120, damping: 16 });
  const ry = useSpring(0, { stiffness: 120, damping: 16 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const cardY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 10);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * 8);
  };

  return (
    <section id="top" ref={ref} onMouseMove={onMove} onMouseLeave={() => { rx.set(0); ry.set(0); }} className="relative min-h-screen flex items-center pt-28 pb-20 overflow-hidden">
      <motion.div style={{ y: glowY, opacity: fade }} className="absolute inset-0 pointer-events-none" aria-hidden>
        <motion.div initial={{ scale: 0.4, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 1.6, ease: EASE }} className="absolute -top-24 -left-24 h-[26rem] w-[26rem] rounded-full bg-iqcyan/25 blur-[110px]" />
        <motion.div initial={{ scale: 0.4, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 1.6, delay: 0.15, ease: EASE }} className="absolute top-1/4 right-[-8rem] h-[24rem] w-[24rem] rounded-full bg-iqpink/20 blur-[110px]" />
        <motion.div initial={{ scale: 0.4, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 1.6, delay: 0.3, ease: EASE }} className="absolute bottom-[-6rem] left-1/3 h-[22rem] w-[22rem] rounded-full bg-iqamber/20 blur-[110px]" />
      </motion.div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1.15fr_0.85fr] gap-16 items-center w-full">
        <div>
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.15, ease: EASE }} className="inline-flex items-center gap-2 border border-ink/15 rounded-full px-4 py-1.5 mb-8 bg-cream/60 backdrop-blur">
            <Sparkles className="h-3.5 w-3.5 text-iqamber" />
            <span className="text-xs font-semibold tracking-[0.25em] uppercase text-ink/70">{t.badge}</span>
          </motion.div>

          <div key={lang}>
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-balance">
              <MaskLine delay={0.25}>{h1a}</MaskLine>
              <MaskLine delay={0.4}>
                <span>
                  {h1b}{" "}
                  <span className="relative inline-block font-serifa italic font-normal text-iqblue">
                    {h1c}
                    <motion.svg viewBox="0 0 220 14" className="absolute -bottom-2 left-0 w-full" aria-hidden>
                      <motion.path d="M4 10 Q 110 -2 216 8" stroke="#E84393" strokeWidth="5" fill="none" strokeLinecap="round" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.9, delay: 1.35, ease: "easeOut" }} />
                    </motion.svg>
                  </span>
                </span>
              </MaskLine>
            </h1>
          </div>

          <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.85, ease: EASE }} className="mt-7 max-w-xl text-base sm:text-lg text-ink/65 leading-relaxed">
            {heroSub}
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 1, ease: EASE }} className="mt-10 flex flex-wrap items-center gap-4">
            <button data-testid="hero-primary-cta" onClick={onStart} className="group flex items-center gap-2.5 bg-ink text-paper font-semibold px-7 py-4 rounded-full hover:bg-iqblue transition-colors duration-300">
              {t.cta1}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button data-testid="hero-secondary-cta" onClick={onStart} className="font-semibold px-7 py-4 rounded-full border border-ink/20 hover:border-ink hover:bg-ink hover:text-paper transition-colors duration-300">
              {t.cta2}
            </button>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.25 }} className="mt-14 grid grid-cols-3 max-w-md gap-6">
            {t.stats.map(([n, l]) => (
              <div key={l} className="border-l-2 border-ink/15 pl-4">
                <div className="font-display font-bold text-2xl">{n}</div>
                <div className="text-xs text-ink/55 mt-1 leading-snug">{l}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0, y: 60, rotate: 4 }} animate={{ opacity: 1, y: 0, rotate: 0 }} transition={{ duration: 1.3, delay: 0.55, ease: EASE }} className="relative hidden lg:block" style={{ perspective: 1100 }}>
          <motion.div style={{ rotateX: rx, rotateY: ry, y: cardY }} className="relative will-change-transform">
            <div className="rounded-[2.2rem] bg-white p-5 shadow-[0_40px_90px_-20px_rgba(10,22,51,0.35)] border border-ink/5">
              <img src="/brightiq-logo.png" alt="Bright IQ — mind in five colours" className="w-full rounded-[1.6rem]" data-testid="hero-logo-card" />
            </div>
            {ORBITS.map(({ Icon, color, pos, dur }) => (
              <motion.div
                key={color}
                className={`absolute ${pos} h-12 w-12 rounded-2xl bg-white shadow-lg shadow-ink/15 border border-ink/5 flex items-center justify-center`}
                animate={{ y: [0, -12, 0] }}
                transition={{ duration: dur, repeat: Infinity, ease: "easeInOut" }}
              >
                <Icon className="h-5 w-5" style={{ color }} />
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
